import React, { useEffect, useRef } from "react";
import { API_URL } from "../config";
import { getNameDay } from "../data/czechNameDays";

const MONTH_NAMES_GEN = [
  "ledna", "února", "března", "dubna", "května", "června",
  "července", "srpna", "září", "října", "listopadu", "prosince"
];

function getTodayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateCz(dateStr) {
  const parts = dateStr.split("-");
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return `${d.getDate()}. ${MONTH_NAMES_GEN[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * DailyMessagePopup
 *
 * Props:
 *   message    {string}   – text citátu/zprávy
 *   userId     {string}   – ID přihlášeného uživatele
 *   onDismiss  {function} – callback po zavření (označí jako viděno na serveru)
 *   isDarkMode {boolean}
 */
export default function DailyMessagePopup({ message, userId, onDismiss, isDarkMode }) {
  const todayStr = getTodayStr();
  const nameDay = getNameDay(todayStr);
  const overlayRef = useRef(null);

  // Zavření kliknutím mimo panel
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      handleClose();
    }
  };

  // Zavření klávesou Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const handleClose = async () => {
    // Označit jako viděno na serveru (cross-device)
    try {
      await fetch(`${API_URL}/daily-messages/seen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, date: todayStr })
      });
    } catch (_) {
      // Tiše selže – popup se nezobrazí znovu díky localStorage fallbacku
    }
    onDismiss();
  };

  const parts = todayStr.split("-");
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const dayNames = ["neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota"];
  const dayName = dayNames[d.getDay()];

  return (
    /* Backdrop – rozmazané pozadí */
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", backgroundColor: "rgba(0,0,0,0.45)" }}
    >
      {/* Panel */}
      <div
        className={`relative w-full max-w-2xl mx-4 rounded-2xl shadow-2xl overflow-hidden ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
        }`}
        style={{ animation: "dmPopIn 0.25s cubic-bezier(.25,.8,.25,1) both" }}
      >
        {/* Modrý pruh nahoře */}
        <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        <div className="px-7 pt-6 pb-7">
          {/* Datum */}
          <div className={`text-xs font-semibold uppercase tracking-widest mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}>
            {dayName}, {formatDateCz(todayStr)}
          </div>

          {/* Svátek */}
          {nameDay && (
            <div className={`flex items-center gap-2 mb-5 px-3 py-2 rounded-lg text-sm font-medium ${
              isDarkMode ? "bg-gray-700 text-blue-300" : "bg-blue-50 text-blue-700"
            }`}>
              <span className="text-lg">🎂</span>
              <span>Dnes má svátek <strong>{nameDay}</strong></span>
            </div>
          )}

          {/* Citát / zpráva */}
          {message && message.trim() ? (
            <div className={`relative pl-5 ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}>
              {/* Svislá čára vlevo */}
              <div className="absolute left-0 top-1 bottom-1 w-1 rounded-full bg-gradient-to-b from-blue-400 to-purple-400" />
              <p className="text-base leading-relaxed whitespace-pre-wrap italic">
                {message}
              </p>
            </div>
          ) : (
            <p className={`text-sm italic ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              Pro dnešní den není žádná zpráva.
            </p>
          )}

          {/* Tlačítko zavřít */}
          <button
            onClick={handleClose}
            className="mt-7 w-full py-2.5 rounded-lg font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white transition"
          >
            Rozumím ✓
          </button>
        </div>

        {/* Křížek */}
        <button
          onClick={handleClose}
          className={`absolute top-4 right-4 text-xl leading-none rounded-full w-7 h-7 flex items-center justify-center transition ${
            isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          }`}
          aria-label="Zavřít"
        >
          ×
        </button>
      </div>

      {/* Inline animace */}
      <style>{`
        @keyframes dmPopIn {
          from { opacity: 0; transform: scale(0.92) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
